// seatSecond — ตรวจ Second Cashier (ไม่เกิน N เครื่องตาม setting cashier_2nd)
//
// ต้องรู้โควตาจาก settings ซึ่ง bootstrap โหลดทีหลัง จึงเริ่มหลัง app:mounted แล้วรอ
// settings มาก่อน ระหว่างนั้น Second ใช้งานได้ตามปกติ (ไม่มีงานเบื้องหลังที่ทำให้ข้อมูลซ้ำ)
//
//   มีที่ / จองได้ → ใช้ต่อ + heartbeat
//   เต็ม / ร้านไม่มีสิทธิ์ → public/seat/ask-2nd-cashier-override.html (แย่งจากเครื่องไหน / เปลี่ยนโหมด)
//   ใช้อยู่แล้วโดนเตะ → ล้างโหมด → public/seat/2nd-cashier-is-taken.html
//   หน้า login / app1 ของร้าน beta → ไม่ตรวจ (ดู decideSkipSeatCheck)
import { watch } from "vue";
import type { Router } from "vue-router";
import { RunMode } from "../../../interface/Pos";
import { SETTING_ID } from "../../../interface/Setting";
import { resolveStoreId } from "../../core/composables/useStoreIdentity";
import { useRunningMode } from "../../core/composables/useRunningMode";
import { isDevReadOnly } from "../../core/utils/devStoreOverride";
import { useSettingsStore } from "../../setting/stores/settings";
import { SEAT_MODE_SECOND, resolveCashier2ndQuota } from "../utils/seatRules";
import { DEVICE_BLOCKED_KEY, hasKey } from "../utils/seatStorage";
import { getSeatIdentity, guardSeat } from "../services/seatGuard";
import { shouldSkipSeatCheck } from "../services/seatScope";

/** settings ไม่มาภายในนี้ = ปล่อยใช้ไปก่อน (ไม่จองที่นั่ง) */
const SETTINGS_WAIT_MS = 30 * 1000;

export default defineNuxtPlugin({
  name: "seat-second",
  setup(nuxtApp) {
    const router = nuxtApp.$router as Router;
    nuxtApp.hooks.hookOnce("app:mounted", () => {
      void start(router);
    });
  },
});

async function start(router: Router) {
  try {
    if (hasKey(DEVICE_BLOCKED_KEY)) return;

    const storeId = await resolveStoreId();
    if (!storeId || isDevReadOnly()) return;
    if (useRunningMode().getMode() !== RunMode.CASHIER_2ND) return;

    const identity = getSeatIdentity(storeId);
    if (!identity) return;
    if (await shouldSkipSeatCheck(storeId, router)) return;

    const settingsStore = useSettingsStore();
    if (!(await waitForSettings(settingsStore))) {
      console.warn("[seatSecond] settings ไม่มา ยังไม่ตรวจที่นั่ง Second");
      return;
    }

    await guardSeat({
      storeId,
      mode: SEAT_MODE_SECOND,
      ...identity,
      getQuota: () =>
        settingsStore.settings
          ? resolveCashier2ndQuota(settingsStore.getValueBysettingId(SETTING_ID.CASHIER_2ND, false))
          : null,
    });
  } catch (error) {
    console.warn("[seatSecond] ตรวจที่นั่งไม่สำเร็จ ปล่อยใช้ไปก่อน", error);
  }
}

function waitForSettings(settingsStore: ReturnType<typeof useSettingsStore>): Promise<boolean> {
  if (settingsStore.settings) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      resolve(false);
    }, SETTINGS_WAIT_MS);
    const stop = watch(
      () => settingsStore.settings,
      (value) => {
        if (!value) return;
        clearTimeout(timer);
        stop();
        resolve(true);
      }
    );
  });
}
